import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { SlideGroupsMap } from '@/features/_data/useUnitData';
import { FrontendSlide } from '@/types/SlideType';
import { IconArrowLeft, IconLoading } from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { T } from '@/ui/Text/Text';
import { triggerFileUploadDialog } from '@/utils/triggerFileUploadDialog';
import { useToast } from '@/ui/Toast/Toast';
import { StyledLoadingWrapper } from '../SlidesEditor/styles';

const StyledWrapper = styled(Flex)`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
`;

const StyledContentWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  img,
  video {
    max-height: calc(100vh - 200px);
    max-width: 100%;
    border-radius: 17px;
    position: relative;
    z-index: 1;
  }

  background: linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5));
  border-radius: 17px;
  align-self: center;
`;

type Props = {
  readonly slideGroupsState: SlideGroupsMap;
  readonly onUpdateSlide: (data: FrontendSlide) => string;

  readonly selected: {
    readonly slideGroupId: string;
    readonly slideId: string;
  };
  readonly setSelected: React.Dispatch<
    React.SetStateAction<{
      readonly slideGroupId: string;
      readonly slideId: string;
    } | null>
  >;
};

// eslint-disable-next-line react/function-component-definition
export const TouchScreenEditor = ({
  slideGroupsState,
  selected,
  setSelected,
  onUpdateSlide,
}: Props) => {
  const slide =
    slideGroupsState[selected.slideGroupId].slides[selected.slideId];
  const [hasMediaLoaded, setHasMediaLoaded] = useState(false);
  const isVideo = slide.contentType?.includes('video');
  const toast = useToast();
  const intl = useIntl();

  const handleVideoUpload = (uploadedFile: File) => {
    // This functionality needs user testing
    const videoURL = URL.createObjectURL(uploadedFile);
    const newVideoSlideId = onUpdateSlide({
      ...slide,
      index: slide?.index ?? 100000,
      asset: videoURL,
      name: slide.name || uploadedFile.name,
      contentType: uploadedFile.type,
      size: uploadedFile.size,
      file: uploadedFile,
    });
    setSelected({ ...selected, slideId: newVideoSlideId });
  };

  const handleImageUpload = (uploadedFile: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(uploadedFile);

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const newSlideId = onUpdateSlide({
          ...slide,
          index: slide?.index ?? 100000,
          asset: reader.result,
          name: slide.name || uploadedFile.name,
          contentType: uploadedFile.type,
          size: uploadedFile.size,
          file: uploadedFile,
        });
        setSelected({ ...selected, slideId: newSlideId });
      }
    };

    reader.onerror = () => {
      console.error('could not parse file', reader);
    };
  };

  return (
    <StyledWrapper direction="column" gap={32} grow padding={32}>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={8}>
          <Button mode="secondary" onClick={() => setSelected(null)}>
            <IconArrowLeft />
          </Button>

          <T h1>{slide?.name}</T>
        </Flex>

        <Button
          onClick={async () => {
            const file = await triggerFileUploadDialog(toast, intl);
            if (file) {
              const uploadedFile = file;
              if (uploadedFile.type.includes('video')) {
                // This functionality needs user testing
                handleVideoUpload(uploadedFile);
              } else {
                handleImageUpload(uploadedFile);
              }

              return;
            }

            console.debug('cancelled');
          }}
          size="large"
        >
          <FormattedMessage
            defaultMessage="Replace {type}"
            id="o86Bz4"
            values={{
              type: isVideo ? 'Video' : 'Image',
            }}
          />
        </Button>
      </Flex>
      <StyledContentWrapper>
        <StyledLoadingWrapper style={{ position: 'absolute' }}>
          <IconLoading />
        </StyledLoadingWrapper>

        {isVideo ? (
          <video
            style={{
              transition: 'opacity 0.5s',
              opacity: hasMediaLoaded ? '1' : '0',
            }}
            autoPlay
            loop={slide?.videoOptions?.loop ?? false}
            controls
            muted
            onLoadedData={() => setHasMediaLoaded(true)}
            src={slide.asset}
          />
        ) : (
          <img
            style={{
              transition: 'opacity 0.5s',
              opacity: hasMediaLoaded ? '1' : '0',
            }}
            onLoad={() => setHasMediaLoaded(true)}
            alt={slide.name}
            src={slide.asset}
          />
        )}
      </StyledContentWrapper>
    </StyledWrapper>
  );
};
